import { generateClient } from 'aws-amplify/api';

/**
 * Organisation reference lists that HR maintains: Job Title, Team/Department and Employment Type.
 *
 * These were hardcoded TypeScript constants, which meant adding a team was a code change and a
 * deploy, and HR could not do it at all. They are now server-backed documents.
 *
 * Reads stay synchronous. They are consulted while rendering form dropdowns and while
 * normalising a stored employment type, so localStorage is a cache of the server: `refresh()`
 * fills it and the setters write through. Same split as CompOffService and LeavePolicyService,
 * for the same reason.
 */

const TEAMS_KEY = 'creyenteone:org-teams:v1';
const EMPLOYMENT_TYPES_KEY = 'creyenteone:org-employment-types:v1';
const JOB_TITLES_KEY = 'creyenteone:org-job-titles:v1';
const HOLIDAYS_KEY = 'creyenteone:org-holidays:v1';
const SESSION_TIMEOUT_KEY = 'creyenteone:session-timeout:v1';

/** Config keys as stored server-side. Kept identical to the cache keys so there is one name. */
export const ORG_CONFIG_KEYS = {
  teams: TEAMS_KEY,
  employmentTypes: EMPLOYMENT_TYPES_KEY,
  jobTitles: JOB_TITLES_KEY,
  holidays: HOLIDAYS_KEY,
  sessionTimeout: SESSION_TIMEOUT_KEY,
} as const;

/**
 * Session inactivity timeout policy, set by an admin and applied org-wide.
 *
 * Stored as a single JSON document (not a list) under SESSION_TIMEOUT_KEY. `minutes` is the
 * idle period after which an authenticated session is signed out; `warnSeconds` is how long the
 * "about to sign out" warning shows first. Bounds are enforced on both read and write so a
 * malformed or out-of-range stored value can never produce an unlimited session — it falls back
 * to the documented default instead.
 */
export interface SessionTimeoutConfig {
  enabled: boolean;
  minutes: number;
}

export const SESSION_TIMEOUT_MIN_MINUTES = 5;
export const SESSION_TIMEOUT_MAX_MINUTES = 120;
/** Default policy: OFF until an admin turns it on, 15 minutes when it is on. */
export const SESSION_TIMEOUT_DEFAULT: SessionTimeoutConfig = { enabled: false, minutes: 15 };

/** Clamp/validate a raw config into a safe SessionTimeoutConfig (never unlimited). */
export const normalizeSessionTimeout = (raw: any): SessionTimeoutConfig => {
  const minutesRaw = Number(raw?.minutes);
  const minutes = Number.isFinite(minutesRaw)
    ? Math.min(SESSION_TIMEOUT_MAX_MINUTES, Math.max(SESSION_TIMEOUT_MIN_MINUTES, Math.round(minutesRaw)))
    : SESSION_TIMEOUT_DEFAULT.minutes;
  return { enabled: raw?.enabled === true, minutes };
};

/**
 * What a fresh install starts with, and the fallback whenever the server has nothing.
 *
 * These are the values that used to be hardcoded, so behaviour before the first deploy or first
 * HR edit is exactly what it was.
 */
export const DEFAULT_TEAMS: string[] = [
  'Fair Observe',
  'Fair Upgrade',
  'Platform',
  'Cloud Ops',
  'Creyente One',
  'HR',
  'CEO',
  'Administration',
];

/**
 * Full Time / Intern / Consultant.
 *
 * 'Intern' is load-bearing beyond being a label: leaveAccrualEngine.isInternEmploymentType
 * matches on the word to apply the Intern leave schedule (no EL, no CL, SL capped at 6). Renaming
 * it here does not rename it there, so a rename would silently move interns onto the full-time
 * schedule. That is why the page warns before letting it be renamed or removed.
 */
export const DEFAULT_EMPLOYMENT_TYPES: string[] = ['Full Time', 'Intern', 'Consultant'];

/**
 * Job Titles, the list behind the User Management "Job Title" dropdown.
 *
 * Unlike Teams and Employment Types this list has no hardcoded predecessor — Job Title was a
 * free-text box, so every organisation already holds values that were never on any list. These
 * are only a starting point for an empty install; the dropdown also offers whatever titles
 * employees already hold (see UserManagement), so nothing existing becomes unselectable while HR
 * curates the list here.
 */
export const DEFAULT_JOB_TITLES: string[] = [
  'Software Engineer',
  'Senior Software Engineer',
  'QA Engineer',
  'DevOps Engineer',
  'Business Analyst',
  'Project Manager',
  'HR Executive',
  'Finance Executive',
  'Intern',
];

/** One published public holiday. Mirrors the shape data/holidays.ts has always exported. */
export interface OrgHoliday {
  /** YYYY-MM-DD. */
  date: string;
  name: string;
}

/**
 * Read a cached list of holidays.
 *
 * Separate from readList because these are objects, not strings: a holiday without a date is
 * unusable and a date without a name has nothing to show, so both are required and a malformed
 * entry is dropped rather than rendered as "undefined" on somebody's calendar.
 *
 * Sorted on the way out so every consumer gets chronological order without asking, which is what
 * the hardcoded module guaranteed by hand.
 *
 * Unlike the string lists, an EMPTY stored array is honoured rather than replaced by the
 * fallback. An organisation genuinely may publish no holidays for a year, and treating that as
 * "not configured" would silently reinstate a list HR had deliberately cleared — the opposite of
 * the Employment Type case, where an empty list makes it impossible to save a user at all.
 */
const readHolidays = (fallback: OrgHoliday[]): OrgHoliday[] => {
  try {
    const raw = localStorage.getItem(HOLIDAYS_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return fallback;
    return parsed
      .map(entry => ({
        date: String(entry?.date ?? '').trim(),
        name: String(entry?.name ?? '').trim(),
      }))
      .filter(h => /^\d{4}-\d{2}-\d{2}$/.test(h.date) && h.name)
      .sort((a, b) => a.date.localeCompare(b.date));
  } catch {
    return fallback;
  }
};

let client: any = null;
const getClient = () => {
  if (!client) client = generateClient();
  return client;
};

const listOrgConfigQuery = /* GraphQL */ `
  query ListOrgConfig {
    listOrgConfig { configKey value updatedBy updatedAt }
  }
`;

const putOrgConfigMutation = /* GraphQL */ `
  mutation PutOrgConfig($configKey: String!, $value: AWSJSON!, $updatedBy: String) {
    putOrgConfig(configKey: $configKey, value: $value, updatedBy: $updatedBy) { configKey }
  }
`;

/**
 * Read a cached list, falling back to the shipped default.
 *
 * An empty stored array falls back too. A reference list with nothing in it is almost certainly
 * a mistake rather than an intent, and an empty Employment Type dropdown would make it
 * impossible to save a user at all.
 */
const readList = (key: string, fallback: string[]): string[] => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return fallback;
    const cleaned = parsed
      .map(v => String(v ?? '').trim())
      .filter(Boolean);
    return cleaned.length > 0 ? cleaned : fallback;
  } catch {
    return fallback;
  }
};

export class OrgConfigService {
  /** The Team / Department list HR maintains. */
  static getTeams(): string[] {
    return readList(TEAMS_KEY, DEFAULT_TEAMS);
  }

  /** The Employment Type list HR maintains. */
  static getEmploymentTypes(): string[] {
    return readList(EMPLOYMENT_TYPES_KEY, DEFAULT_EMPLOYMENT_TYPES);
  }

  /** The Job Title list HR maintains. */
  static getJobTitles(): string[] {
    return readList(JOB_TITLES_KEY, DEFAULT_JOB_TITLES);
  }

  /**
   * Pull both lists from the server into the cache.
   *
   * Returns false when the server could not be reached, so a management screen can say it is
   * showing cached lists rather than presenting local values as the organisation's.
   */
  static async refresh(): Promise<boolean> {
    try {
      const result = await getClient().graphql({
        query: listOrgConfigQuery,
        authMode: 'userPool' as const,
      } as any);
      const docs = ('data' in result ? result.data : null)?.listOrgConfig;
      if (!Array.isArray(docs)) return false;

      const known = new Set<string>([
        TEAMS_KEY,
        EMPLOYMENT_TYPES_KEY,
        JOB_TITLES_KEY,
        HOLIDAYS_KEY,
        SESSION_TIMEOUT_KEY,
      ]);
      let changed = false;
      for (const doc of docs) {
        const key = String(doc?.configKey || '');
        if (!known.has(key)) continue;
        // AWSJSON arrives as a JSON *string*; stored verbatim so readList can parse it.
        const raw = typeof doc.value === 'string' ? doc.value : JSON.stringify(doc.value);
        if (raw && raw !== localStorage.getItem(key)) {
          localStorage.setItem(key, raw);
          changed = true;
        }
      }
      if (changed) window.dispatchEvent(new Event('org-config-updated'));
      return true;
    } catch (error) {
      console.warn('[OrgConfig] refresh failed, using cached lists:', error);
      return false;
    }
  }

  /**
   * Replace a list, cache first then server.
   *
   * Entries are trimmed and de-duplicated case-insensitively before saving: two teams differing
   * only in case would both save happily and then never match the same employee record, which is
   * the kind of near-duplicate that quietly splits a report in two.
   *
   * `ok` reports whether the write reached the server, so the caller can distinguish "saved for
   * everyone" from "saved on this device only".
   */
  private static async putList(
    key: string,
    values: string[],
    updatedBy: string
  ): Promise<{ ok: boolean; saved: string[] }> {
    const seen = new Set<string>();
    const saved: string[] = [];
    for (const value of values) {
      const trimmed = String(value ?? '').trim();
      if (!trimmed) continue;
      const fingerprint = trimmed.toLowerCase();
      if (seen.has(fingerprint)) continue;
      seen.add(fingerprint);
      saved.push(trimmed);
    }

    localStorage.setItem(key, JSON.stringify(saved));
    window.dispatchEvent(new Event('org-config-updated'));

    try {
      await getClient().graphql({
        query: putOrgConfigMutation,
        variables: { configKey: key, value: JSON.stringify(saved), updatedBy },
        authMode: 'userPool' as const,
      } as any);
      return { ok: true, saved };
    } catch (error) {
      console.warn(`[OrgConfig] "${key}" saved locally but not on the server:`, error);
      return { ok: false, saved };
    }
  }

  static saveTeams(values: string[], updatedBy: string) {
    return this.putList(TEAMS_KEY, values, updatedBy);
  }

  static saveEmploymentTypes(values: string[], updatedBy: string) {
    return this.putList(EMPLOYMENT_TYPES_KEY, values, updatedBy);
  }

  static saveJobTitles(values: string[], updatedBy: string) {
    return this.putList(JOB_TITLES_KEY, values, updatedBy);
  }

  /**
   * The published public-holiday calendar.
   *
   * `fallback` is what data/holidays.ts shipped as a constant, passed in by that module rather
   * than duplicated here — this service owns storage, not the company's calendar.
   */
  static getHolidays(fallback: OrgHoliday[]): OrgHoliday[] {
    return readHolidays(fallback);
  }

  /**
   * Replace the holiday calendar, cache first then server — the same order as putList.
   *
   * De-duplicated by DATE, not by the whole entry: two names on one date is a data-entry slip
   * (a renamed holiday saved twice), and keeping both would make a single day appear twice on
   * every calendar that lists them. The first spelling wins, as it does for the string lists.
   */
  static async saveHolidays(
    values: OrgHoliday[],
    updatedBy: string
  ): Promise<{ ok: boolean; saved: OrgHoliday[] }> {
    const seen = new Set<string>();
    const saved: OrgHoliday[] = [];
    for (const entry of values) {
      const date = String(entry?.date ?? '').trim();
      const name = String(entry?.name ?? '').trim();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !name || seen.has(date)) continue;
      seen.add(date);
      saved.push({ date, name });
    }
    saved.sort((a, b) => a.date.localeCompare(b.date));

    localStorage.setItem(HOLIDAYS_KEY, JSON.stringify(saved));
    window.dispatchEvent(new Event('org-config-updated'));

    try {
      await getClient().graphql({
        query: putOrgConfigMutation,
        variables: { configKey: HOLIDAYS_KEY, value: JSON.stringify(saved), updatedBy },
        authMode: 'userPool' as const,
      } as any);
      return { ok: true, saved };
    } catch (error) {
      console.warn('[OrgConfig] holidays saved locally but not on the server:', error);
      return { ok: false, saved };
    }
  }

  /**
   * The org-wide session inactivity timeout policy (a single JSON document, not a list).
   *
   * Reads synchronously off the cache like the lists. An absent or malformed value yields the
   * safe default (feature OFF, 15 minutes) rather than an unlimited session — the caller's idle
   * timer only arms when `enabled` is true, and `minutes` is always clamped to [5, 120].
   */
  static getSessionTimeout(): SessionTimeoutConfig {
    try {
      const raw = localStorage.getItem(SESSION_TIMEOUT_KEY);
      if (!raw) return { ...SESSION_TIMEOUT_DEFAULT };
      return normalizeSessionTimeout(JSON.parse(raw));
    } catch {
      return { ...SESSION_TIMEOUT_DEFAULT };
    }
  }

  /** Save the session timeout policy, cache first then server — the same order as putList. */
  static async saveSessionTimeout(
    config: SessionTimeoutConfig,
    updatedBy: string
  ): Promise<{ ok: boolean; saved: SessionTimeoutConfig }> {
    const saved = normalizeSessionTimeout(config);
    localStorage.setItem(SESSION_TIMEOUT_KEY, JSON.stringify(saved));
    window.dispatchEvent(new Event('org-config-updated'));
    try {
      await getClient().graphql({
        query: putOrgConfigMutation,
        variables: { configKey: SESSION_TIMEOUT_KEY, value: JSON.stringify(saved), updatedBy },
        authMode: 'userPool' as const,
      } as any);
      return { ok: true, saved };
    } catch (error) {
      console.warn('[OrgConfig] session timeout saved locally but not on the server:', error);
      return { ok: false, saved };
    }
  }

  /**
   * Employment types whose removal or rename changes leave entitlement rather than just a label.
   *
   * 'Full Time' and 'Intern' are both matched by name downstream — Intern by
   * isInternEmploymentType, Full Time as the effective default everywhere else — so the page
   * confirms before touching them instead of letting a rename silently re-band employees.
   */
  static readonly LEAVE_AFFECTING_EMPLOYMENT_TYPES = ['Full Time', 'Intern'];

  /** Whether removing/renaming this employment type would change how leave is calculated. */
  static affectsLeaveCalculation(value: string): boolean {
    return this.LEAVE_AFFECTING_EMPLOYMENT_TYPES.some(
      t => t.toLowerCase() === String(value || '').trim().toLowerCase()
    );
  }
}
